import { useState, useRef, useMemo, useEffect } from 'react';
import { enablePatches, enableMapSet, produceWithPatches } from 'immer';
import { clone, isEqual } from 'lodash-es';

// src/module/store/react/index.ts
function useUnmount(callback, deps) {
  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  useEffect(() => {
    return () => {
      if (!isMounted.current) {
        callback();
      }
    };
  }, []);
}
enablePatches();
enableMapSet();
function createSubscription(store, subscriber, stateSelector) {
  let lastState = store.getState(stateSelector);
  const queue = [];
  let subscriberIsBusy = false;
  const act = () => {
    const nextState = store.getState(stateSelector);
    if (isEqual(nextState, lastState)) {
      return;
    }
    subscriberIsBusy = true;
    subscriber(nextState, lastState);
    subscriberIsBusy = false;
    lastState = nextState;
  };
  return () => {
    if (subscriberIsBusy) {
      queue.push(() => act());
      return;
    }
    act();
    while (queue.length) {
      const nextAct = queue.shift();
      nextAct?.();
    }
  };
}
var Store = class {
  _subscribers = [];
  initialState;
  state;
  _fireSubscribers() {
    for (const subscriber of this._subscribers) {
      subscriber();
    }
  }
  constructor(initialState) {
    const copy = clone(initialState);
    this.initialState = [copy];
    this.state = copy;
  }
  /**
   * Subscribe to state updates
   *
   * @param stateSelector Function to select a subset of the state
   * @param subscriber Function that passes the next and previous states
   * @returns
   */
  subscribe(stateSelector, subscriber) {
    const sub = createSubscription(this, subscriber, stateSelector);
    this._subscribers.push(sub);
    const unsub = () => {
      this._subscribers = this._subscribers.filter((s) => s !== sub);
    };
    return unsub;
  }
  getInitialState() {
    return clone(this.initialState);
  }
  setInitialState(state, reset = true) {
    this.initialState.push(state);
    if (reset) {
      this.resetToInitialState();
    }
  }
  resetToInitialState() {
    const lastInitialState = this.getInitialState().at(-1);
    if (!lastInitialState) {
      throw new Error("Could not find initial state!");
    }
    this.state = lastInitialState;
    this._fireSubscribers();
  }
  setState(arg) {
    if (arg instanceof Function) {
      const [result, patches] = produceWithPatches(this.state, arg);
      if (!patches.length) {
        return result;
      }
      this.state = result;
    } else {
      this.state = clone(arg);
    }
    this._fireSubscribers();
    return this.state;
  }
  /**
   * @deprecated - Old method name. Use `setState` instead.
   */
  update = this.setState;
  getState(stateSelector) {
    const currentState = clone(this.state);
    return stateSelector ? stateSelector(currentState) : currentState;
  }
  /**
   * @deprecated - Old method name. Use `getState` instead.
   */
  getCurrentState = this.getState;
  when(stateSelector, assertFn) {
    return new Promise((resolve) => {
      const currentState = this.getState(stateSelector);
      if (assertFn(currentState)) {
        return resolve(currentState);
      }
      const unsub = this.subscribe(stateSelector, (nextState) => {
        if (assertFn(nextState)) {
          unsub();
          return resolve(nextState);
        }
      });
    });
  }
};

// src/module/store/react/index.ts
function useStore(store, stateSelector) {
  const [state, setState] = useState(store.getState(stateSelector));
  const isMounted = useRef(true);
  const unsub = useMemo(() => {
    return store.subscribe(stateSelector, (nextState) => {
      if (!isMounted.current) {
        return;
      }
      setState(() => nextState);
    });
  }, []);
  useUnmount(() => {
    isMounted.current = false;
    unsub();
  });
  return state;
}
var Store2 = class extends Store {
  constructor(...args) {
    super(...args);
    this.useState = this.useState.bind(this);
  }
  useState(stateSelector) {
    return useStore(this, stateSelector);
  }
};

export { Store2 as Store, useStore };
